import { Injectable } from '@angular/core';
import { VerbalService } from 'src/app/modules/read-aloud/services/verbal.service';
import { Scene, SceneEvent } from 'src/app/shared/types/scene';
import { ActivityService } from '../activity/activity.service';
import { SceneComponent } from 'src/app/shared/components';
import { filter, firstValueFrom, take } from 'rxjs';
import { AudioService } from 'src/app/modules/audio/services/audio/audio.service';

@Injectable({
  providedIn: 'root',
})
export class SceneService {
  sceneComponent: SceneComponent | undefined;
  scene: Scene | undefined;
  private isInterrupted = false;
  private showTimeLogs = false;
  private sceneStartTime = 0;
  private eventTimers: Map<string, number> = new Map();

  constructor(
    private verbalService: VerbalService,
    private activityService: ActivityService,
    private audioService: AudioService
  ) {}

  interruptScene(): void {
    this.isInterrupted = true;
    // Stop any playing audio
    this.audioService.stopAllSounds();
  }

  clearSceneState(): void {
    this.sceneComponent = undefined;
    this.scene = undefined;
    this.isInterrupted = false;
    this.sceneStartTime = 0;
    this.eventTimers.clear();
  }

  private startEventTimer(eventKey: string): void {
    if (this.showTimeLogs) {
      this.eventTimers.set(eventKey, performance.now());
    }
  }

  private endEventTimer(eventKey: string): void {
    if (this.showTimeLogs) {
      const startTime = this.eventTimers.get(eventKey);
      if (startTime) {
        const endTime = performance.now();
        const duration = endTime - startTime;
        console.log(
          `⏱️ Event "${eventKey}" completed in ${duration.toFixed(2)}ms`
        );
        this.eventTimers.delete(eventKey);
      }
    }
  }

  private startSceneTimer(): void {
    if (this.showTimeLogs) {
      this.sceneStartTime = performance.now();
      console.log('🎬 Scene execution started');
    }
  }

  private endSceneTimer(): void {
    if (this.showTimeLogs && this.sceneStartTime > 0) {
      const endTime = performance.now();
      const totalDuration = endTime - this.sceneStartTime;
      console.log(
        `🎬 Total scene execution time: ${totalDuration.toFixed(2)}ms`
      );
      this.sceneStartTime = 0;
    }
  }

  async initializeScene(sceneComponent: SceneComponent): Promise<void> {
    if (!sceneComponent || !sceneComponent.sceneDef) {
      console.warn('Scene is undefined');
      return Promise.reject(new Error('Scene is undefined'));
    }

    // Clear any previous scene state to ensure isolation
    this.clearSceneState();

    this.sceneComponent = sceneComponent;
    this.scene = sceneComponent.sceneDef;

    console.log('Initializing scene: ', sceneComponent.sceneDef);

    // iterate over each event
    const eventKeys = Object.keys(this.scene?.events ?? {});
    const allDialoguesInitialized = eventKeys.map(async eventKey => {
      const event = this.scene?.events?.[eventKey];
      if (!event) {
        return;
      }

      // initialize spoken dialogue
      if (event.dialogue) {
        try {
          await this.initializeDialogue(event);
        } catch (error) {
          console.error('Error initializing event', eventKey, error);
        }
      }

      // initialize SFX
      if (event.sfx) {
        try {
          event.sfxAudioData = {
            id: event.sfx,
            url: event.sfx,
            containsPhonemes: false,
          };
          await this.audioService.initSound(event.sfxAudioData);
        } catch (error) {
          console.error('Error initializing SFX', eventKey, error);
        }
      }
    });

    await Promise.all(allDialoguesInitialized);

    return Promise.resolve();
  }

  async initializeDialogue(event: SceneEvent): Promise<void> {
    if (event.dialogue) {
      // Create a copy of the dialogue to avoid mutating the original
      let processedDialogue = event.dialogue;

      if (processedDialogue.includes('{TARGET_TEXT}')) {
        const targetText = this.activityService.currActivity?.targetText;
        if (targetText) {
          processedDialogue = processedDialogue.replaceAll(
            '{TARGET_TEXT}',
            targetText
          );
        }
      }

      if (processedDialogue.includes('${LEARNING_TARGET}')) {
        const learningTarget =
          this.activityService.currActivity?.startScreenData
            ?.learningTarget?.[0]?.learningTarget.text;
        if (learningTarget) {
          processedDialogue = processedDialogue.replaceAll(
            '${LEARNING_TARGET}',
            learningTarget
          );
        }
      }

      if (processedDialogue.includes('${SUCCESS_CRITERIA}')) {
        const successCriteria: string[] =
          this.activityService.currActivity?.startScreenData?.learningTarget?.[0].successCriteria?.map(
            sc => sc.text
          ) ?? [];
        if (successCriteria.length > 0) {
          processedDialogue = processedDialogue.replaceAll(
            '${SUCCESS_CRITERIA}',
            successCriteria.join(', and ').concat('.')
          );
        }
      }

      event.dialogueAudioData =
        this.verbalService.replaceLearningTargetAndSuccessCriteriaTokens(
          processedDialogue,
          'scene-dialogue'
        );
      await this.verbalService.loadBatch([event.dialogueAudioData]);
      return Promise.resolve();
    }

    return Promise.reject(new Error('No dialogue found in event: ' + event));
  }

  async executeScene(): Promise<void> {
    if (!this.sceneComponent || !this.scene) {
      console.error('No scene component or scene set.');
      return Promise.reject(new Error('No scene component or scene set.'));
    }

    console.log('Executing scene: ', this.scene);
    this.isInterrupted = false;
    this.startSceneTimer();

    const eventKeys = Object.keys(this.scene.events ?? {});
    await this.executeSceneEvents(eventKeys);

    this.endSceneTimer();
    return Promise.resolve();
  }

  private async executeSceneEvents(eventKeys: string[]): Promise<void> {
    for (const eventKey of eventKeys) {
      if (this.isInterrupted) {
        this.endSceneTimer();
        return Promise.resolve();
      }

      console.log('Executing event: ', eventKey);
      this.startEventTimer(eventKey);

      const event = this.scene?.events?.[eventKey];
      if (!event) {
        this.endEventTimer(eventKey);
        continue;
      }

      if (event.eventStartCallback) {
        event.eventStartCallback();
      }

      const promises: Promise<void>[] = [];
      if (event.animEventSequence) {
        promises.push(this.executeEventAnimation(event));
      }

      promises.push(this.executeEventAudio(event));

      // Wait for all audio and animations to complete
      await Promise.all(promises);

      // Check if scene was interrupted after audio/animations
      if (this.isInterrupted) {
        this.endEventTimer(eventKey);
        this.endSceneTimer();
        return Promise.resolve();
      }

      // Wait for the event's delayMS before moving to next event
      if (event.delayMS) {
        await new Promise(resolve => setTimeout(resolve, event.delayMS));
      }

      // Call event end callback if it exists
      if (event.eventEndCallback) {
        event.eventEndCallback();
      }

      this.endEventTimer(eventKey);
    }

    return Promise.resolve();
  }

  private async executeEventAudio(event: SceneEvent): Promise<void> {
    const promises: Promise<void>[] = [];

    if (event.sfxAudioData) {
      promises.push(
        this.audioService.playSound(event.sfxAudioData, true).then(() =>
          firstValueFrom(
            this.audioService.playbackEndId.pipe(
              filter(id => id === event.sfxAudioData?.id),
              take(1)
            )
          ).then(() => void 0)
        )
      );
    }

    if (event.dialogueAudioData) {
      promises.push(
        this.verbalService.play(event.dialogueAudioData).then(() => {
          if (
            event.dialogueAudioData &&
            'type' in event.dialogueAudioData &&
            event.dialogueAudioData.type === 'sequence'
          ) {
            return firstValueFrom(
              this.verbalService.sequenceEnded$.pipe(
                filter(id => id === event.dialogueAudioData?.id),
                take(1)
              )
            ).then(() => void 0);
          }
          return firstValueFrom(
            this.audioService.playbackEndId.pipe(
              filter(id => id === event.dialogueAudioData?.id),
              take(1)
            )
          ).then(() => void 0);
        })
      );
    }

    await Promise.all(promises);
  }

  private async executeEventAnimation(event: SceneEvent): Promise<void> {
    if (!event.animEventSequence || !this.sceneComponent) {
      return;
    }

    const sequence = event.animEventSequence;
    for (const [, animEvent] of Object.entries(sequence)) {
      if (this.isInterrupted) {
        return;
      }

      this.sceneComponent.currentCharacter = animEvent.character;
      this.sceneComponent.currentAnim = animEvent.anim;
      this.sceneComponent.currentAnimLoop = animEvent.loop ?? true;
      this.sceneComponent.currentAnimFlipHorz = animEvent.flipHorz ?? false;
      this.sceneComponent.currentAnimFlipVert = animEvent.flipVert ?? false;

      if (animEvent.durationMS) {
        await new Promise(resolve => setTimeout(resolve, animEvent.durationMS));
      }
    }
  }
}
